import { useState } from 'react';
import { useData } from '../../../contexts/DataContext';
import { useConfirm, useToast } from '../../ui';
import { apiUtils } from '../../../utils/apiUtils';
import { DELETION_STATUS_REFRESH } from './deletionEvents';

/** Preflight, confirmation and the 202 handoff for both deletion targets. */
export function useClientDeletion() {
  const [pendingId, setPendingId] = useState(null);
  const { deleteAccount, deleteClient } = useData();
  const confirm = useConfirm();
  const toast = useToast();

  const beginDeletion = async (type, target, onAccepted) => {
    if (pendingId) return false;
    const id = target?._id;
    if (!id) return false;
    setPendingId(id);
    let submitted = false;
    try {
      const response = await apiUtils.get(`/api/${type === 'client' ? 'clients' : 'accounts'}/${id}/deletion-impact`);
      const payload = await response.json();
      if (!payload.success || !payload.data) throw new Error(payload.error || 'Could not check what will be deleted');
      const impact = payload.data;
      if (impact.deletionState === 'deleting') {
        toast.info('This deletion is already in progress. Check its status below.');
        return false;
      }
      const isClient = type === 'client';
      const identity = isClient ? impact.name : `${impact.platform} @${impact.handle}`;
      const limits = 'This cannot be undone. Backups expire under their normal retention schedule. Files already exported or sent to other services cannot be recalled.';
      const description = isClient
        ? `${identity} has ${impact.accounts} account${impact.accounts === 1 ? '' : 's'} and ${impact.schedules} monitoring schedule${impact.schedules === 1 ? '' : 's'}. Its live RepCheck data, scans, pulses, notes and reports will be permanently removed. ${limits}`
        : `All live scans, pulses, notes, reports and monitoring for ${identity} will be permanently removed. ${limits}`;
      const approved = await confirm({
        title: `Delete ${identity}?`,
        description,
        confirmLabel: isClient ? 'Delete client' : 'Delete account',
        destructive: true,
        ...(isClient ? { confirmationText: impact.name, confirmationLabel: 'Type this client name to confirm' } : {}),
      });
      if (!approved) return false;
      submitted = true;
      const operation = isClient ? await deleteClient(id, impact.name) : await deleteAccount(id, { handle: impact.handle, platform: impact.platform });
      window.dispatchEvent(new Event(DELETION_STATUS_REFRESH));
      toast.info('Deletion started', { description: 'Monitoring has stopped. Track progress in the deletion status section.' });
      onAccepted?.(operation);
      return true;
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Please try again.';
      if (submitted) window.dispatchEvent(new Event(DELETION_STATUS_REFRESH));
      const uncertain = submitted && (!error.response || error.response.status >= 500);
      toast.error(uncertain ? 'Could not confirm deletion request' : 'Could not start deletion', {
        description: uncertain ? 'Check deletion status before trying again. The request may have been received.' : message,
      });
      return false;
    } finally {
      setPendingId(null);
    }
  };

  return { beginDeletion, pendingId };
}
